import debounce from 'lodash/debounce';
import each from 'lodash/each';
import find from 'lodash/find';
import includes from 'lodash/includes';
import inRange from 'lodash/inRange';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import reduce from 'lodash/reduce';
import Data from '../data';
import Utils from '../utils';

const {
	CalculatedData,
	Carrier,
	ServiceType,
	PackageType,
	InternalCapability,
} = Data;
const { Session, Util } = Utils;

class RateAdvisorUtil {
	// default rate-advisor user preference
	static _rateAdvisorUserPref = {
		services: {},

		// default Package filter checked
		packages: {
			packages: true,
			largeEnvelopes: false,
			flatRateEnvelopes: false,
		},

		// default other filter options
		otherOptions: {
			includedCoverage: false,
			trackable: false,
			freePickup: false,
			guaranteedDeliveryDate: false,
			guaranteedDeliveryTime: false,
			saturdayDelivery: false,
			sundayDelivery: false,
			includedCoverageSelection: 'any',
		},

		// default `true` to show "About Media Mail" modal
		// when user checks "Media Mail" in the filter.
		// https://stamps.atlassian.net/browse/WEBAPPS-26886
		showMediaMailSelectionModal: true,

		// the following two properties will not be stored in user preference
		// the data is used is in 'getPreferredServicesToDisplay'
		eta: {
			min: null,
			max: null,
		},

		price: {
			min: null,
			max: null,
		},
	};

	/**
	 * Make a call to save user's preference data with 1 second delay.
	 */
	static _saveRateAdvisorData = debounce(async data => {
		await Session.savePreferences([
			{
				Name: 'RateAdvisorData',
				Value: JSON.stringify(data),
			},
		]);
	}, 1000);

	/**
	 * Return user's rate-advisor preference data, or default object if
	 * it's not defined for new users.
	 * @param {object|null} userPref User's rate advisor preference data.
	 * @returns {object}
	 */
	static getRateAdvisorUserPreference = userPref => {
		if (isObject(userPref)) {
			this._rateAdvisorUserPref = {
				...this._rateAdvisorUserPref,
				...userPref,
			};
		}

		return {
			...this._rateAdvisorUserPref,
		};
	};

	/**
	 * Save user's rate-advisor preference data.
	 * @params {object} userPreference
	 */
	static saveRateAdvisorUserPreference = (field, data) => {
		// merge the data into _rateAdvisorUserPref object
		if (/services|packages|otherOptions|eta|price/.test(field)) {
			this._rateAdvisorUserPref = {
				...this._rateAdvisorUserPref,
				[field]: isObject(data) ? data : this._rateAdvisorUserPref[field],
			};
		} else if (field === 'showMediaMailSelectionModal') {
			this._rateAdvisorUserPref = {
				...this._rateAdvisorUserPref,
				[field]: isBoolean(data) ? data : this._rateAdvisorUserPref[field],
			};
		}

		// make a call to save some filter changes; eta and price will not be stored
		if (
			/services|packages|otherOptions|showMediaMailSelectionModal/.test(field)
		) {
			const { services, packages, otherOptions, showMediaMailSelectionModal } =
				this._rateAdvisorUserPref;

			this._saveRateAdvisorData({
				services,
				packages,
				otherOptions,
				showMediaMailSelectionModal,
			});
		}

		return {
			...this._rateAdvisorUserPref,
		};
	};

	/**
	 * Build the payload for GetPrints API.
	 * @param {object} ratingData Parameters coming from the wrapper component.
	 * @returns {object}
	 */
	static buildGetRatesPayload = ratingData => {
		const {
			weightOz,
			weightLb,
			length,
			width,
			height,
			shipDate,
			fromCity,
			fromState,
			fromZip,
			toStreet1,
			toFullName,
			toCity,
			toState,
			toZip,
			toCountryCode,
			toProvince,
			toPostalCode,
			residentialIndicator,
		} = ratingData;

		// check if the country code is domestic
		const isCountryCodeDomestic = /AS|GU|MH|MP|PW|PR|VI|US/i.test(
			toCountryCode
		);

		return {
			from_address: {
				city: fromCity,
				state_province: fromState,
				postal_code: fromZip,
				country_code: 'US',
			},
			to_address: {
				name: toFullName,
				address_line1: toStreet1,
				city: toCity,
				state_province: isCountryCodeDomestic ? toState : toProvince,
				postal_code: isCountryCodeDomestic ? toZip : toPostalCode,
				country_code: toCountryCode,
				residential_indicator: residentialIndicator ? 'yes' : 'no',
			},
			package: {
				// weight
				weight: weightLb * 16 + weightOz,
				weight_unit: 'ounce',

				// dimensions
				length,
				width,
				height,
				dimension_unit: 'inch',
			},
			ship_date: shipDate,
		};
	};

	static processRates = (rates, upsGroundETA) => {
		const carrierCapabilities = Session.getCarrierCapabilities();

		const result = reduce(
			Util.toCamelCase(rates),
			(_result, rate) => {
				// get the Service static data
				const serviceStaticData = CalculatedData.getService(
					rate.serviceType,
					rate.packagingType
				);

				/**
				 * Dev note:
				 * Exclude the following rates:
				 * 1. Letter
				 * 2. Postcard
				 * 3. FedEx, regardless of user's account (per WEBAPPS-26630)
				 * 4. Any service are included in Data module
				 * 5. Any service that is not enabled for user's account
				 */

				if (
					rate.packagingType === 'Postcard' ||
					rate.packagingType === 'Letter' ||
					rate.packagingType === 'ups_letter' ||
					!isObject(serviceStaticData) ||
					(serviceStaticData.carrier === Carrier.FedEx &&
						!carrierCapabilities[Carrier.FedEx]) ||
					(serviceStaticData.carrier === Carrier.UPS &&
						!carrierCapabilities[Carrier.UPS]) ||
					(serviceStaticData.carrier === Carrier.DHLExpress &&
						!carrierCapabilities[Carrier.DHLExpress]) ||
					(serviceStaticData.carrier === Carrier.GlobalPost &&
						!carrierCapabilities[Carrier.GlobalPost])
				) {
					return _result;
				}

				// update estimated delivery days of 'ups ground'
				if (rate.serviceType === ServiceType.UpsGround) {
					rate.estimatedDeliveryDays =
						isString(upsGroundETA) && upsGroundETA !== ''
							? upsGroundETA
							: rate.estimatedDeliveryDays;
				}

				// update estimated delivery days of 'ups ground saver'
				if (rate.serviceType === ServiceType.UpsGroundSaver) {
					rate.estimatedDeliveryDays =
						isString(upsGroundETA) && upsGroundETA !== ''
							? (parseInt(upsGroundETA) + 1).toString()
							: rate.estimatedDeliveryDays;
				}

				// e.g.
				// estimatedDeliveryDays = "1-2" => [1, 2]
				// estimatedDeliveryDays = "1"   => [1, undefined]
				// estimatedDeliveryDays = null   => [1, 999]
				const estimatedDeliveryDays = isString(rate.estimatedDeliveryDays)
					? rate.estimatedDeliveryDays.match(/\d+/g)?.map(Number)
					: [1, 999]; // services with "varies" delivery days
				const [minDeliverDays, maxDeliverDays] = isArray(estimatedDeliveryDays)
					? estimatedDeliveryDays
					: [];

				if (isNumber(minDeliverDays)) {
					_result.etaRange.push(minDeliverDays);
				}

				if (isNumber(maxDeliverDays)) {
					_result.etaRange.push(maxDeliverDays);
				}

				if (isNumber(rate.shipmentCost?.totalAmount)) {
					_result.priceRange.push(rate.shipmentCost.totalAmount);
				}

				_result.rates.push({
					// service rate data from server
					...rate,

					// service data from CalculatedData
					...serviceStaticData,

					// service ETA
					minDeliverDays,
					maxDeliverDays,

					// included service options
					includedServiceOptions: this.buildIncludedServiceOptions(
						rate,
						serviceStaticData
					),
				});

				return _result;
			},
			{
				rates: [],
				etaRange: [],
				priceRange: [],
			}
		);

		// sort ranges in ascending order
		result.etaRange.sort((n1, n2) => n1 - n2);
		result.priceRange.sort((n1, n2) => n1 - n2);

		// reset eta and price ranges
		this._rateAdvisorUserPref.eta =
			result.etaRange.length > 0
				? {
						min: result.etaRange[0],
						max: result.etaRange[result.etaRange.length - 1],
					}
				: this._rateAdvisorUserPref.eta;

		this._rateAdvisorUserPref.price =
			result.priceRange.length > 0
				? {
						min: result.priceRange[0],
						max: result.priceRange[result.priceRange.length - 1],
					}
				: this._rateAdvisorUserPref.price;

		return {
			rates: result.rates,
			rateAdvisorData: {
				...this._rateAdvisorUserPref,
			},
		};
	};

	/**
	 * Return included service options (or Add-ons) for the service.
	 * See https://stamps.atlassian.net/wiki/spaces/PRODREQ/pages/688195741/Mobile+App+-+Display+Carrier+Services+Card+Details
	 *
	 * @param {object} rate
	 * @param {object} serviceStaticData
	 */
	static buildIncludedServiceOptions = (rate, serviceStaticData) => {
		const { trackable, isGuaranteedService } = rate;
		const { carrier, serviceType, packageShippingOptions } = serviceStaticData;

		// build included service options from carrier-service API data
		const includedServiceOptions = reduce(
			packageShippingOptions,
			(_serviceOptions, option) => {
				if (isArray(option.advancedOptions)) {
					// check if service has "Saturday Delivery" option
					// per WEBAPPS-31244, "Saturday Delivery" option is returned if (1) option exists in advancedOptions, or (2) Carrier is USPS
					_serviceOptions.hasSaturdayDeliveryOption =
						_serviceOptions.hasSaturdayDeliveryOption !== true &&
						(isObject(
							find(option.advancedOptions, { option: 'saturday_delivery' })
						) ||
							carrier === Carrier.USPS);

					// check if service has "Sunday/Holiday Delivery" option
					_serviceOptions.hasSundayDeliveryOption =
						_serviceOptions.hasSundayDeliveryOption !== true &&
						isObject(
							find(option.advancedOptions, { option: 'sunday_delivery' })
						);
				} else {
					_serviceOptions.hasSaturdayDeliveryOption = false;
					_serviceOptions.hasSundayDeliveryOption = false;
				}

				if (isArray(option.insuranceOptions)) {
					const coverageOptions = option.insuranceOptions
						.map(insuranceOption =>
							isNumber(insuranceOption?.includedInsuranceAmount)
								? insuranceOption.includedInsuranceAmount
								: null
						)
						.filter(amount => isNumber(amount))
						.sort((n1, n2) => n2 - n1);

					// check if service has "any Included Coverage" option
					_serviceOptions.hasAnyIncludedCoverageOption =
						_serviceOptions.hasAnyIncludedCoverageOption !== true &&
						coverageOptions.length > 0;

					// check if service has "$100 Included Coverage" option
					_serviceOptions.has100IncludedCoverageOption =
						_serviceOptions.has100IncludedCoverageOption !== true &&
						isNumber(
							find(coverageOptions, amount => amount >= 100 && amount < 200)
						);

					// check if service has "$200 Included Coverage" option
					_serviceOptions.has200IncludedCoverageOption =
						_serviceOptions.has200IncludedCoverageOption !== true &&
						isNumber(find(coverageOptions, amount => amount >= 200));
				} else {
					_serviceOptions.hasAnyIncludedCoverageOption = false;
					_serviceOptions.has100IncludedCoverageOption = false;
					_serviceOptions.has200IncludedCoverageOption = false;
				}

				return _serviceOptions;
			},
			{}
		);

		return {
			hasFreePickupOption:
				carrier === Carrier.USPS ||
				carrier === Carrier.GlobalPost ||
				carrier === Carrier.DHLExpress,
			hasTrackableOption: trackable === 'yes',
			hasGuaranteedDeliveryDateOption: isGuaranteedService === true,
			hasGuaranteedDeliveryTimeOption: includes(
				// TODO: Do new services have this or any other options?
				[
					ServiceType.Ups2ndDayAirAm,
					ServiceType.UpsNextDayAirEarly,
					ServiceType.FedExGround,
					ServiceType.FedEx2DayAm,
					ServiceType.FedExStandardOvernight,
					ServiceType.FedExPriorityOvernight,
					ServiceType.FedExFirstOvernight,
				],
				serviceType
			),
			...includedServiceOptions,
		};
	};

	/**
	 * Return a subset of the data set received from Server based on `rateAdvisorData` object.
	 * https://stamps.atlassian.net/wiki/spaces/PRODREQ/pages/3121468/Rate+Advisor+Modal+-+Functionality
	 * @param {array} serviceRates
	 * @param {object} rateAdvisorData RateAdvisorData data from Session
	 * @returns {array}
	 */
	static getPreferredServicesToDisplay = serviceRates => {
		const rateAdvisorData = this._rateAdvisorUserPref;
		const { services, eta, price, otherOptions } = rateAdvisorData;
		const packageFilterItems = this.getPackageFilterItems();
		const packageTypesLookup = reduce(
			packageFilterItems,
			(_lookup, item) => {
				each(item.packageTypes, packageType => {
					_lookup[packageType] = item.checked === true;
				});

				return _lookup;
			},
			{}
		);

		/**
		 * ETA: 2 - 6 days
		 * service 1 - 6 x
		 * service 2 - 6 v
		 */
		return isEmpty(serviceRates)
			? []
			: serviceRates.filter(service => {
					const {
						serviceType,
						packageType,
						minDeliverDays,
						includedServiceOptions,
						shipmentCost,
					} = service;
					const totalAmount = shipmentCost?.totalAmount;

					// use minDeliverDays when estimatedDeliveryDays is not in a range
					const maxDeliverDays = isNumber(service.maxDeliverDays)
						? service.maxDeliverDays
						: minDeliverDays;
					return (
						services[serviceType] === true &&
						packageTypesLookup[packageType] === true &&
						(isNumber(eta.min) && isNumber(eta.max)
							? inRange(maxDeliverDays, eta.min, eta.max + 1)
							: true) &&
						(isNumber(eta.min) && isNumber(eta.max)
							? inRange(minDeliverDays, eta.min, eta.max + 1)
							: true) &&
						(isNumber(price.min) && isNumber(price.max) && isNumber(totalAmount)
							? inRange(totalAmount, price.min, price.max + 1)
							: true) &&
						(otherOptions.freePickup === true
							? includedServiceOptions.hasFreePickupOption === true
							: true) &&
						(otherOptions.trackable === true
							? includedServiceOptions.hasTrackableOption === true
							: true) &&
						(otherOptions.includedCoverage &&
						otherOptions.includedCoverageSelection === 'any'
							? includedServiceOptions.hasAnyIncludedCoverageOption
							: true) &&
						(otherOptions.includedCoverage &&
						otherOptions.includedCoverageSelection === '100'
							? includedServiceOptions.has100IncludedCoverageOption
							: true) &&
						(otherOptions.includedCoverage &&
						otherOptions.includedCoverageSelection === '200'
							? includedServiceOptions.has200IncludedCoverageOption
							: true) &&
						(otherOptions.guaranteedDeliveryDate === true
							? includedServiceOptions.hasGuaranteedDeliveryDateOption === true
							: true) &&
						(otherOptions.guaranteedDeliveryTime === true
							? includedServiceOptions.hasGuaranteedDeliveryTimeOption === true
							: true) &&
						(otherOptions.saturdayDelivery === true
							? includedServiceOptions.hasSaturdayDeliveryOption === true
							: true) &&
						(otherOptions.sundayDelivery === true
							? includedServiceOptions.hasSundayDeliveryOption === true
							: true)
					);
				});
	};

	/**
	 * Return an array of included service options.
	 * @param {object} service The service data, see 'processRates' method.
	 * @returns {array}
	 */
	static getIncludedServiceOptions = service => {
		const includedOptions = isObject(service?.includedServiceOptions)
			? service.includedServiceOptions
			: {};
		const includedServicesList = [];

		if (includedOptions.has100IncludedCoverageOption) {
			includedServicesList.push({
				text: '$100 Included Coverage',
				icon: 'icon-coverage',
			});
		} else if (includedOptions.has200IncludedCoverageOption) {
			includedServicesList.push({
				text: '$200 Included Coverage',
				icon: 'icon-coverage',
			});
		}

		if (includedOptions.hasTrackableOption) {
			includedServicesList.push({
				text: 'Tracking',
				icon: 'icon-tracking',
			});
		}

		if (includedOptions.hasGuaranteedDeliveryDateOption) {
			includedServicesList.push({
				text: 'Guaranteed Delivery',
				icon: 'icon-guaranteed-delivery-date',
			});
		}

		if (includedOptions.hasGuaranteedDeliveryTimeOption) {
			includedServicesList.push({
				text: 'Guaranteed Delivery (Time Certain)',
				icon: 'icon-guaranteed-delivery-time',
			});
		}

		if (includedOptions.hasFreePickupOption) {
			includedServicesList.push({
				text: 'Free Pickup',
				icon: 'icon-free-pickup',
			});
		}

		if (includedOptions.hasSaturdayDeliveryOption) {
			includedServicesList.push({
				text: 'Saturday Delivery',
				icon: 'icon-saturday-delivery',
			});
		}

		if (includedOptions.hasSundayDeliveryOption) {
			includedServicesList.push({
				text: 'Sunday/Holiday Delivery',
				icon: 'icon-sunday-holiday-delivery',
			});
		}

		return includedServicesList;
	};

	/**
	 * Return an array to render a set of checkboxes in PackageFilter component.
	 * @returns {array}
	 */
	static getPackageFilterItems = () => {
		const { packages } = this._rateAdvisorUserPref;
		const carrierCapabilities = Session.getCarrierCapabilities();

		const packageFilterItems = [
			{
				id: 'packages',
				displayName: 'Packages / Thick Envelopes',
				packageTypes: [PackageType.Package],
				isEligibleToShow: true,
				checked: packages.packages === true,
			},
			{
				id: 'largeEnvelopes',
				displayName: 'Large Envelopes / Flats',
				packageTypes: [
					PackageType.LargeEnvelope,
					PackageType.ExpressEnvelope,
					PackageType.FedExEnvelope,
				],
				isEligibleToShow: true,
				checked: packages.largeEnvelopes === true,
			},
			{
				id: 'flatRateEnvelopes',
				displayName: 'Flat Rate Boxes / Envelopes',
				packageTypes: [
					PackageType.UspsSmallFlatRateBox,
					PackageType.UspsMediumFlatRateBox,
					PackageType.UspsLargeFlatRateBox,
					PackageType.UspsFlatRateEnvelope,
					PackageType.UspsFlatRateEnvelope,
					PackageType.UspsPaddedFlatRateEnvelope,
					PackageType.UspsLegalFlatRateEnvelope,
				],
				isEligibleToShow: true,
				checked: packages.flatRateEnvelopes === true,
			},
			{
				id: 'tubes',
				displayName: 'Tubes',
				packageTypes: [PackageType.UpsTube, PackageType.FedExTube],
				isEligibleToShow:
					carrierCapabilities[Carrier.UPS] === true ||
					carrierCapabilities[Carrier.FedEx] === true,
				checked: packages.tubes === true,
			},
			{
				id: 'paks',
				displayName: 'Paks',
				packageTypes: [PackageType.UpsPak, PackageType.FedExPak],
				isEligibleToShow:
					carrierCapabilities[Carrier.UPS] === true ||
					carrierCapabilities[Carrier.FedEx] === true,
				checked: packages.paks === true,
			},
			{
				id: 'fedexOneRateBoxes',
				displayName: 'One Rate - FedEx Boxes',
				packageTypes: [
					PackageType.FedExOneRateSmallBox,
					PackageType.FedExOneRateMediumBox,
					PackageType.FedExOneRateLargeBox,
					PackageType.FedExOneRateExtraLargeBox,
					PackageType.FedExOneRateEnvelope,
					PackageType.FedExOneRatePak,
					PackageType.FedExOneRateTube,
				],
				isEligibleToShow:
					carrierCapabilities[Carrier.FedEx] === true &&
					Session.hasCapability(InternalCapability.FedexDirect) === true,
				checked: packages.fedexOneRateBoxes === true,
			},
			{
				id: 'fedexBoxes',
				displayName: 'FedEx Boxes',
				packageTypes: [
					PackageType.FedExSmallBox,
					PackageType.FedExMediumBox,
					PackageType.FedExLargeBox,
					PackageType.FedExExtraLargeBox,
				],
				isEligibleToShow: carrierCapabilities[Carrier.FedEx] === true,
				checked: packages.fedexBoxes === true,
			},
			{
				id: 'upsExpressBoxes',
				displayName: 'UPS Express Boxes',
				packageTypes: [
					PackageType.UpsExpressBoxSmall,
					PackageType.UpsExpressBoxMedium,
					PackageType.UpsExpressBoxLarge,
				],
				isEligibleToShow: carrierCapabilities[Carrier.UPS] === true,
				checked: packages.upsExpressBoxes === true,
			},
		];

		return packageFilterItems.filter(item => item.isEligibleToShow === true);
	};
}

export default RateAdvisorUtil;
